import React from 'react';
import _ from 'lodash';
import type { NextPage } from 'next';
import Head from 'next/head';
import { useRouter } from 'next/router';
import { web } from '@wix/wix-common-aspects';
import log from '@wix/wix-log';
import type { AspectStore } from '@wix/wix-aspects';
import {
  Pricing,
  SortingMethod,
} from '@wix/ambassador-partners-profile-v1-partner-profile/types';
import { WixDesignSystemProvider } from '@wix/design-system';
import {
  Category,
  Service,
} from '@wix/ambassador-marketplace-categories-v1-category/types';
import LocationAPI from '@services/LocationAPI';
import { MIN_PRICE } from '@utils/constants';
import {
  Language,
  WixNextApiRequest,
  WixNextApiResponse,
  IParams,
  UserDetails,
  LocaleDataTranslations,
  SEOProps,
  HadronConfig,
} from '@common/types';
import { getCleanUrl } from '@utils/urlUtils';
import { ExploreContextProvider } from '@components/Contexts/ExploreContext';
import { HeaderContextProvider } from '@components/Contexts/HeaderContext';
import { BiSessionContextProvider } from '@components/Contexts/BiSessionContext';
import Explore from '@components/Explore';
import { getServicesFiltersFromUrl, convertServicesToIds } from '@utils/route';
import { convertPricingToUSD } from '@utils/currency';
import createSeoAPI from '@services/ExploreSeoAPI';
import createInitialStateService from '@services/InitialStateService';
import { Place } from '@wix/ambassador-atlas-v2-place/types';
import { getWixContext } from '@utils/async-store';
import { getPageLanguage } from '@app/utils';

interface ExploreHeaderProps {
  title: string;
  currentPage: number;
  metaTags: Array<Record<string, string>>;
  links: Array<Record<string, string>>;
}

const areExploreHeaderPropsEqual = (
  prevProps: ExploreHeaderProps,
  nextProps: ExploreHeaderProps,
): boolean => {
  return (
    prevProps.title === nextProps.title &&
    prevProps.currentPage === nextProps.currentPage &&
    _.isEqual(prevProps.metaTags, nextProps.metaTags) &&
    _.isEqual(prevProps.links, nextProps.links)
  );
};

const ExploreHeader: React.FC<ExploreHeaderProps> = React.memo(
  ({ title, currentPage, metaTags, links }) => {
    const pageTitle =
      currentPage > 1 ? `${title} - Page ${currentPage}` : title;
    const canonicalLink = links.find((link) => link.rel === 'canonical');
    const nonCanonicalLinks = links.filter((link) => link.rel !== 'canonical');

    return (
      <Head>
        <title>{pageTitle}</title>
        {nonCanonicalLinks.map((link, index) => (
          <link
            {...(link.hrefLang && { hrefLang: link.hrefLang })}
            rel={link.rel}
            href={link.href}
            key={`lang-${index}`}
          />
        ))}
        {metaTags.map((meta, index) => (
          <meta {...meta} key={`meta-${index}`} />
        ))}
        {canonicalLink && <link rel="canonical" href={canonicalLink.href} />}
      </Head>
    );
  },
  areExploreHeaderPropsEqual,
);

const Hire: NextPage<{
  services: Array<Service>;
  categoriesForHeader: Array<Service> | undefined;
  languages: Array<Language>;
  language: string;
  staticLocaleUrl: string;
  providers: any;
  selectedPricing: Pricing;
  isMobileUserAgent: boolean;
  selectedLanguages: Array<Language>;
  selectedServices: Array<Service>;
  selectedLocation: Place;
  selectedSortingMethod: SortingMethod;
  category: Category;
  service: Service;
  origin: string;
  entry: string;
  host: string;
  seo: SEOProps;
  userCurrency: string;
  currencyConversionRate: number;
  userDetails: UserDetails;
  userProfileImage: string;
  localeDataTranslations: LocaleDataTranslations;
  currentPage: number;
}> = ({
  isMobileUserAgent,
  services,
  categoriesForHeader,
  selectedPricing,
  selectedLanguages,
  selectedServices,
  languages,
  language,
  staticLocaleUrl,
  userDetails,
  userProfileImage,
  selectedLocation,
  selectedSortingMethod,
  providers: { partnerProfiles, numberOfResults, numberOfPages },
  category,
  service,
  origin,
  entry,
  host,
  seo: { title, metaTags, links },
  userCurrency,
  currencyConversionRate,
  localeDataTranslations,
  currentPage,
}) => {
  const router = useRouter();
  const currentPageFromRouter = router.query.page
    ? Number(router.query.page) || currentPage
    : currentPage;

  return (
    <>
      <ExploreHeader
        title={title}
        currentPage={currentPageFromRouter}
        metaTags={metaTags}
        links={links}
      />
      <BiSessionContextProvider>
        <HeaderContextProvider
          services={categoriesForHeader || services}
          host={host}
          userProfileImage={userProfileImage}
          origin={origin}
          entry={entry}
        >
          <ExploreContextProvider
            languages={languages}
            language={language}
            staticLocaleUrl={staticLocaleUrl}
            services={services}
            selectedPricing={selectedPricing}
            selectedLanguages={selectedLanguages}
            providers={partnerProfiles}
            numberOfResults={numberOfResults}
            numberOfPages={numberOfPages}
            currentPage={currentPage}
            isMobileUserAgent={isMobileUserAgent}
            selectedServices={selectedServices}
            selectedLocation={selectedLocation}
            selectedSortingMethod={selectedSortingMethod}
            userCurrency={userCurrency}
            currencyConversionRate={currencyConversionRate}
            category={category}
            service={service}
            origin={origin}
            entry={entry}
          >
            <WixDesignSystemProvider features={{ newColorsBranding: true }}>
              <Explore host={host} />
            </WixDesignSystemProvider>
          </ExploreContextProvider>
        </HeaderContextProvider>
      </BiSessionContextProvider>
    </>
  );
};

function getPricingFilterFromUrl(query: IParams) {
  const min = _.toNumber(query.min || MIN_PRICE);
  const max = _.toNumber(query.max) || undefined;
  return { min, max };
}

function getSortingMethodFromUrl(query: IParams) {
  const sortingMethodFromUrl = query.sort;
  const _sortingMethod =
    SortingMethod[sortingMethodFromUrl as keyof typeof SortingMethod] ??
    SortingMethod.RELEVANCE;
  return _sortingMethod;
}

function getLanguagesFiltersFromUrl(
  query: IParams,
  languages: Array<Language>,
) {
  const languagesFromUrl = query.lang as string;
  const selectedLanguages = _.filter(languages, (item) =>
    languagesFromUrl?.split(',').includes(item.id),
  );
  return selectedLanguages;
}

function convertLanguagesToIds(givenLanguages: Array<Language>) {
  return _.map(givenLanguages, (language) => language.id);
}

async function getLocationFromUrl(
  query: IParams,
  locationApi: ReturnType<typeof LocationAPI>,
  aspects: AspectStore,
): Promise<Place | undefined> {
  const savedPlaceId = query.location as string;
  if (savedPlaceId) {
    try {
      const response = await locationApi.getPlace(savedPlaceId);
      return response?.place;
    } catch (err: any) {
      const { message, stack } = err;
      log('hire')
        .withAspects(aspects)
        .error(`error w/ hire getLocationFromUrl ${message} ${stack}`);
    }
  } else {
    return undefined;
  }
}

export async function getServerSideProps({
  req,
  res,
  query,
}: {
  req: WixNextApiRequest;
  res: WixNextApiResponse;
  query: IParams;
}) {
  const geo = req.aspects?.get(web.Geo);
  const url = req.aspects?.get(web.Url);

  const pair = _.size(_.get(query, 'all')) > 0 ? _.get(query, 'all') : [];
  const categoryName = pair![0];
  const serviceName = pair![1];

  const wixContext = getWixContext();

  const initialStateService = createInitialStateService(req.aspects, res);
  const servicesAPI = wixContext.serviceFactory.servicesAPI();
  const languagesAPI = wixContext.serviceFactory.languagesAPI();
  const providersAPI = wixContext.serviceFactory.providersAPI();
  const currencyAPI = wixContext.serviceFactory.currencyAPI();
  const locationAPI = wixContext.serviceFactory.locationAPI();

  const selectedLocation = await getLocationFromUrl(
    query,
    locationAPI,
    req.aspects,
  );

  try {
    const { entry, origin } = query;
    const page = query.page as string;
    const serviceIds = query.serviceIds as string;

    const language = getPageLanguage({
      aspects: req.aspects,
      pageName: 'hire',
    });

    const [
      allServicesWithHierarchy,
      service,
      category,
      categoriesForHeader,
      languages,
    ] = await Promise.all([
      servicesAPI.getAllServicesWithHierarchy({
        withPathName: true,
        withTranslatedKey: true,
        language,
      }),
      servicesAPI.getServiceByName(serviceName),
      servicesAPI.getCategoryByName(categoryName),
      servicesAPI.getCategoriesForHeader({
        withPathName: true,
        withTranslatedKey: true,
        language,
      }),
      languagesAPI.getLanguages(),
    ]);

    const selectedPricing = getPricingFilterFromUrl(query);
    const selectedLanguages = getLanguagesFiltersFromUrl(query, languages);
    const selectedServices = getServicesFiltersFromUrl(
      query,
      allServicesWithHierarchy,
      service,
      category,
    );

    const { currencyConversionRate, userCurrency } =
      await currencyAPI.getGeoBasedTaxAndCurrencyRate({
        aspects: req.aspects,
        loggerName: '/hire',
      });
    const selectedPricingInUSD = convertPricingToUSD(
      selectedPricing,
      currencyConversionRate,
    );

    const selectedSortingMethod = getSortingMethodFromUrl(query);
    const providers = await providersAPI.getProviders({
      serviceIds: convertServicesToIds(selectedServices),
      languages: convertLanguagesToIds(selectedLanguages),
      pricing: selectedPricingInUSD,
      page: query.page || '1',
      sortingMethod: selectedSortingMethod,
      selectedLocation,
    });

    const hireSeoAPI = await createSeoAPI({
      categoryName,
      serviceName,
      query,
      url: getCleanUrl(allServicesWithHierarchy, url, serviceIds),
      page,
      language,
      numberOfPages: providers.numberOfPages,
    });

    const config = res.locals.appContext.config.load<HadronConfig>(
      'experts-marketplace-nextjs-template.json',
    );
    const staticLocaleUrl = await res.locals.hadron.staticUrl(
      'com.wixpress.locale-dataset-data',
      config.clientTopology.locale_dataset_data_static_url,
    );

    const initialState = await initialStateService.create();

    const props = {
      initialState,
      userCurrency,
      currencyConversionRate,
      services: allServicesWithHierarchy,
      categoriesForHeader,
      languages,
      host: `https://${req.get('host')}`,
      seo: {
        title: hireSeoAPI.getPageTitle(),
        metaTags: hireSeoAPI.getPageMetaTags(),
        links: hireSeoAPI.getPageLinkTags(),
      },
      staticLocaleUrl,
      currentPage: parseInt(page) || 1,
      ...(selectedLanguages && { selectedLanguages }),
      ...(selectedPricing && {
        selectedPricing: JSON.parse(JSON.stringify(selectedPricing)),
      }),
      ...(selectedLocation && {
        selectedLocation: JSON.parse(JSON.stringify(selectedLocation)),
      }),
      ...(selectedServices && { selectedServices }),
      ...(selectedSortingMethod && { selectedSortingMethod }),
      ...(providers && { providers: JSON.parse(JSON.stringify(providers)) }),
      ...(geo && { geo }),
      ...(service && { service }),
      ...(category && { category }),
      ...(entry && { entry }),
      ...(origin && { origin }),
    };
    return {
      props,
    };
  } catch (e: any) {
    const { message, stack } = e;
    log('hire')
      .withAspects(req.aspects)
      .error(`error w/ hire`, { message, stack });
    return {
      props: { error: message, providers: [], seo: {} },
    };
  }
}

export default Hire;
